/**
 * Application constants
 * Centralized configuration values and constants
 */

/**
 * Application metadata
 */
export const APP_CONFIG = {
  NAME: 'Personal Website',
  VERSION: '1.0.0',
  AUTHOR: 'Ertugrul Kücükkaya',
  DESCRIPTION: 'My opening to the wide world',
} as const;

/**
 * Environment configuration
 */
export const ENV = {
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
} as const;

/**
 * Route paths
 */
export const ROUTES = {
  HOME: '/',
  BLOG: '/blog',
  BOOKS: '/books',
  PROJECTS: '/projects',
  LEGACY: '/legacy',
} as const;

/**
 * Navigation items
 */
export const NAVIGATION_ITEMS = [
  { path: ROUTES.BLOG, label: 'Blog' },
  { path: ROUTES.BOOKS, label: 'Books' },
  { path: ROUTES.PROJECTS, label: 'Projects' },
  { path: ROUTES.LEGACY, label: 'Legacy' },
] as const;

/**
 * Animation durations (in milliseconds)
 */
export const ANIMATION = {
  TYPING_SPEED: 150,
  PAUSE_BETWEEN_NAMES: 300,
  CURSOR_HIDE_DELAY: 500,
  HEADER_SHOW_DELAY: 1000,
} as const;

/**
 * Layout breakpoints (in pixels)
 */
export const BREAKPOINTS = {
  MOBILE: 480,
  TABLET: 768,
  DESKTOP: 1024,
  LARGE_DESKTOP: 1200,
} as const;

/**
 * API configuration
 */
export const API_CONFIG = {
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

/**
 * Content limits
 */
export const CONTENT_LIMITS = {
  BLOG_POSTS_PER_PAGE: 10,
  PROJECTS_PER_PAGE: 12,
  BOOKS_PER_PAGE: 20,
  MAX_TITLE_LENGTH: 200,
  MAX_EXCERPT_LENGTH: 300,
  MIN_CONTENT_LENGTH: 10,
} as const;

/**
 * Validation rules
 */
export const VALIDATION = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  URL_PROTOCOLS: ['http:', 'https:'],
  RATING_MIN: 1,
  RATING_MAX: 5,
} as const;

/**
 * Book statuses
 */
export const BOOK_STATUS = {
  CURRENTLY_READING: 'currently_reading',
  COMPLETED: 'completed',
  WANT_TO_READ: 'want_to_read',
} as const;

/**
 * Project statuses
 */
export const PROJECT_STATUS = {
  COMPLETED: 'completed',
  IN_PROGRESS: 'in_progress',
  PLANNED: 'planned',
} as const;

/**
 * Storage bucket names
 */
export const STORAGE_BUCKETS = {
  BLOG_IMAGES: 'blog-images',
  PROJECT_IMAGES: 'project-images',
  LEGACY_FILES: 'legacy-files',
  BOOK_COVERS: 'book-covers',
} as const;

/**
 * Error codes
 */
export const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  THEME: 'website-theme',
  USER_PREFERENCES: 'user-preferences',
  LAST_VISIT: 'last-visit',
} as const;

/**
 * SEO configuration
 */
export const SEO = {
  DEFAULT_TITLE: 'Ertugrul Kücükkaya - Personal Website',
  TITLE_SEPARATOR: ' | ',
  DEFAULT_DESCRIPTION: 'Personal website showcasing projects, blog posts, book reviews, and professional journey',
  DEFAULT_KEYWORDS: ['personal website', 'portfolio', 'blog', 'projects', 'books', 'technology'],
  DEFAULT_AUTHOR: 'Ertugrul Kücükkaya',
} as const;

/**
 * Social media links (placeholder - update with actual links)
 */
export const SOCIAL_LINKS = {
  GITHUB: 'https://github.com/industrystark',
  LINKEDIN: 'https://linkedin.com/in/ertugrulkucukkaya',
  TWITTER: 'https://twitter.com/notexisting',
} as const;

/**
 * Date formats
 */
export const DATE_FORMATS = {
  DISPLAY: 'MMMM d, yyyy',
  ISO: 'yyyy-MM-dd',
  FULL: 'EEEE, MMMM d, yyyy',
  TIME: 'HH:mm:ss',
  DATETIME: 'yyyy-MM-dd HH:mm:ss',
} as const;

/**
 * Theme colors (CSS custom properties)
 */
export const THEME_COLORS = {
  PRIMARY: '--color-primary',
  SECONDARY: '--color-secondary',
  ACCENT: '--color-accent',
  BACKGROUND: '--color-background',
  SURFACE: '--color-surface',
  TEXT_PRIMARY: '--color-text-primary',
  TEXT_SECONDARY: '--color-text-secondary',
  BORDER: '--color-border',
  ERROR: '--color-error',
  WARNING: '--color-warning',
  SUCCESS: '--color-success',
  INFO: '--color-info',
} as const;
